import { authService, type PartnerAccount } from '@/entities/auth/api/auth.api';
import {
  AUTH_STORAGE_KEY,
  clearAuthSession,
  getAccessToken,
  saveAccessToken,
} from '@/shared/api/authSession';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface AuthState {
  user: PartnerAccount | null;
  login: (loginEmail: string, password: string) => Promise<void>;
  /** 서버 로그아웃 후 로컬 세션 정리. API 실패해도 로컬은 항상 종료한다. */
  logout: () => Promise<void>;
  /** 상단바 정보 갱신 — 어드민에서 업체명 등이 바뀌었을 수 있다 */
  refreshMe: () => Promise<void>;
}

/** 토큰이 살아 있고 계정 정보가 있으면 로그인 상태 */
export const selectIsAuthenticated = (state: AuthState) =>
  state.user != null && getAccessToken() != null;

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,

      // 로그인 응답에 상단바 정보가 함께 오므로 me를 다시 부르지 않는다
      login: async (loginEmail, password) => {
        const { token, expiresIn, ...account } = await authService.login({
          loginEmail: loginEmail.trim(),
          password,
        });
        saveAccessToken(token, expiresIn);
        set({ user: account });
      },

      logout: async () => {
        try {
          await authService.logout();
        } finally {
          clearAuthSession();
          set({ user: null });
        }
      },

      refreshMe: async () => {
        const user = await authService.me();
        set({ user });
      },
    }),
    {
      name: AUTH_STORAGE_KEY,
      partialize: (state) => ({ user: state.user }),
    },
  ),
);
